import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpenCheck, ChevronDown, Compass, Hammer, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useMmText } from "../../hooks/useMmText";
import { config } from "../../Environment";
import {
  realityShaping,
  type MmRegister,
  type RealityShapingTheme,
} from "../../api/mentalModels";
import { createTrait } from "../../api/userTraits";
import { Button } from "../ui/button";

/**
 * ICDF §5.2 Reality-Shaping — formative assessment of challenges and
 * experiences. Synthesizes the period's friction visits, journey reflections
 * and captured insights into practical-wisdom themes, each routable to:
 * learn (marketplace search) / gap (aspiration trait) / design (a design
 * visit on a captured thought). Nothing auto-commits — the learner picks.
 */

interface ThemeMatch {
  _id: string;
  name: string;
  type?: string;
}

interface Props {
  register: MmRegister;
  /** Route a "design" theme into the studio's capture flow, prefilled. */
  onDesign: (statement: string) => void;
}

const MmRealityShaping: React.FC<Props> = ({ register, onDesign }) => {
  const { tm } = useMmText(register);
  const { i18n } = useTranslation();
  const retryFetch = useRetryFetch();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [themes, setThemes] = useState<RealityShapingTheme[] | null>(null);
  const [counts, setCounts] = useState<{ frictions: number; reflections: number; insights: number } | null>(null);
  const [matches, setMatches] = useState<Record<number, ThemeMatch[] | "loading">>({});
  const [adopted, setAdopted] = useState<Record<number, boolean>>({});

  const synthesize = async () => {
    setLoading(true);
    setThemes(null);
    setMatches({});
    setAdopted({});
    try {
      const res = await realityShaping(retryFetch, {
        periodDays: 30,
        language: i18n.language?.split("-")[0] || "en",
        register,
      });
      setThemes(res.themes);
      setCounts(res.counts);
    } catch (e) {
      console.error(e);
      toast.error(tm("rs_failed", "Could not read the period — try again later."));
    } finally {
      setLoading(false);
    }
  };

  const findMatches = async (i: number, theme: RealityShapingTheme) => {
    setMatches((m) => ({ ...m, [i]: "loading" }));
    try {
      const response = await retryFetch(`${config.API_BASE}/api/rag/search/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ query: theme.searchQuery || theme.theme, limit: 3 }),
      });
      const body = await response.json();
      setMatches((m) => ({ ...m, [i]: (body.results || []).slice(0, 3) }));
    } catch {
      setMatches((m) => ({ ...m, [i]: [] }));
    }
  };

  const adoptGap = async (i: number, theme: RealityShapingTheme) => {
    try {
      await createTrait(retryFetch, {
        name: theme.theme.slice(0, 100),
        description: theme.whatItAsks?.slice(0, 500),
        category: "aspiration",
        source: "autoenginuity",
        tags: ["reality-shaping"],
      });
      setAdopted((a) => ({ ...a, [i]: true }));
      toast.success(tm("rs_gap_adopted", "Added to the gaps you're closing — see your Compass."));
    } catch (e: any) {
      toast.error(e.message || "Could not save");
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-border/70 bg-white">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-start"
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Compass className="w-4 h-4 text-primary/60" />
          {tm("rs_title", "What is this period asking of you?")}
        </span>
        <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            {tm(
              "rs_intro",
              "Reads your last month — the thoughts friction pulled you into, your journey reflections, your saved insights — and names the practical wisdom they keep asking for."
            )}
          </p>
          {!themes && (
            <Button size="sm" onClick={synthesize} disabled={loading}>
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin me-1.5" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 me-1.5" />
              )}
              {tm("rs_run", "Read this period")}
            </Button>
          )}

          {themes && themes.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {tm(
                "rs_not_enough",
                "Not enough experience recorded this month yet — come back after a few more visits, reflections or insights."
              )}
            </p>
          )}

          {themes && themes.length > 0 && (
            <>
              {counts && (
                <p className="text-[11px] text-muted-foreground">
                  {tm("rs_counts", "Read: {{f}} frictions · {{r}} reflections · {{i}} insights")
                    .replace("{{f}}", String(counts.frictions))
                    .replace("{{r}}", String(counts.reflections))
                    .replace("{{i}}", String(counts.insights))}
                </p>
              )}
              {themes.map((theme, i) => {
                const themeMatches = matches[i];
                return (
                  <div key={i} className="rounded-xl border border-border/70 p-3.5 space-y-2">
                    <p className="text-sm font-semibold">{theme.theme}</p>
                    <p className="text-xs text-foreground/80 leading-relaxed">{theme.whatItAsks}</p>
                    {theme.evidence && (
                      <p className="text-[11px] text-muted-foreground italic">{theme.evidence}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Button
                        size="sm"
                        variant={theme.route === "learn" ? "default" : "outline"}
                        onClick={() => findMatches(i, theme)}
                        disabled={themeMatches === "loading"}
                      >
                        {themeMatches === "loading" ? (
                          <Loader2 className="w-3 h-3 animate-spin me-1.5" />
                        ) : (
                          <BookOpenCheck className="w-3 h-3 me-1.5" />
                        )}
                        {tm("rs_route_learn", "Find ways to grow it")}
                      </Button>
                      <Button
                        size="sm"
                        variant={theme.route === "gap" ? "default" : "outline"}
                        onClick={() => adoptGap(i, theme)}
                        disabled={Boolean(adopted[i])}
                      >
                        <Sparkles className="w-3 h-3 me-1.5" />
                        {adopted[i]
                          ? tm("rs_gap_done", "Adopted as a gap")
                          : tm("rs_route_gap", "Adopt as a gap")}
                      </Button>
                      <Button
                        size="sm"
                        variant={theme.route === "design" ? "default" : "outline"}
                        onClick={() => onDesign(`${theme.theme} — ${theme.whatItAsks}`)}
                      >
                        <Hammer className="w-3 h-3 me-1.5" />
                        {tm("rs_route_design", "Design a response")}
                      </Button>
                    </div>
                    {Array.isArray(themeMatches) && (
                      <div className="space-y-1 pt-1">
                        {themeMatches.length === 0 ? (
                          <p className="text-[11px] text-muted-foreground">
                            {tm("rs_no_matches", "No matching activities found for this yet.")}
                          </p>
                        ) : (
                          themeMatches.map((m) => (
                            <a
                              key={m._id}
                              href={`/marketplace/product/${m._id}`}
                              className="block rounded-lg border border-border/60 px-2.5 py-1.5 text-xs hover:border-primary/40 transition-colors"
                            >
                              {m.name}
                            </a>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                onClick={synthesize}
                className="text-[11px] text-primary/70 hover:text-primary cursor-pointer"
                disabled={loading}
              >
                {tm("rs_rerun", "read again")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MmRealityShaping;
